import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Chip,
  ClickAwayListener,
  Collapse,
  Fade,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import useHandleTrackEvent from 'hooks/useHandleTrackEvent';
import { updateUiState } from '@/redux/slices/uiSlice';
import { activeNavSurface, hoverNavSurface } from '../../../../navSurface';

/** A group's children that can be offered: present and not disabled. */
const flyoutItemsOf = (items) =>
  (items || []).filter((item) => item && !item?.disabled);

const NavItem = ({
  id,
  title,
  items,
  href,
  startIcon,
  action,
  highlighted,
  showCounts,
  onNavClick,
  dataTour,
  navGroup,
  variant,
  railLabel,
  railSelected,
  onRailSelect,
  standalone = false,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { pathname, search } = location;
  const dashboardSidebarExpanded = useSelector(
    (state) => state.uiStates.dashboardSidebarExpanded,
  );
  const currentContextData = useSelector(
    (state) => state.uiStates.currentContextData,
  );
  const loggedInUserTeamDetails = useSelector(
    (state) => state.uiStates.loggedInUserTeamDetails,
  );
  const pinnedTeams = useSelector((state) => state.uiStates.pinnedTeams);
  const navCollapsedGroups = useSelector(
    (state) => state.uiStates.navCollapsedGroups,
  );
  const isTourRunning = useSelector((state) => state.productTour.isRunning);
  const currentTeamToManage = useSelector(
    (state) => state.uiStates.currentTeamToManage,
  );
  const dispatch = useDispatch();
  const handleTrackEvent = useHandleTrackEvent();
  const { orgConfigs } = useSelector((state) => state.org.orgConfigs);
  const userDetails = useSelector((state) => state.userDetails.userDetails);

  const pathRegex = new RegExp(`^${href}$`, 'i');
  const isGroupActive = pathRegex.test(pathname) || highlighted;

  // Whether this group has a child list worth showing at all. It is the
  // long-standing pathname/title allowlist, lifted out of JSX unchanged so
  // that the collapse toggle below can ask the same question the renderer
  // does -- a group with nothing to show must not offer a chevron.
  const hasVisibleItems =
    standalone ||
    ((pathname === `/practice/interview` ||
      pathname === `/practice/pitch` ||
      pathname === `/practice/script` ||
      pathname === `/sales-call-analysis` ||
      pathname.startsWith('/teams') ||
      pathname.startsWith('/deals') ||
      pathname.startsWith('/legacy/deals') ||
      // regex1.test(pathname) ||
      // regex2.test(pathname) ||
      // regex3.test(pathname) ||
      pathname === `/teams/${currentTeamToManage?.id}/sales-call-analysis` ||
      pathname === `/teams/${currentTeamToManage?.id}/conversation-ai` ||
      pathname === `/teams/${currentTeamToManage?.id}/team-settings` ||
      pathname === `/cro-interviews` ||
      pathname === `/accounts` ||
      pathname === `/compass-framework` ||
      pathname === `/compass-helps` ||
      pathname === `/tasks` ||
      pathname === `/digital-twin` ||
      pathname === `/ask-ai` ||
      pathname === `/conversation-ai` ||
      pathname === `/settings` ||
      pathname === `/profile-settings` ||
      pathname === `/customer/settings` ||
      pathname === `/performance` ||
      // pathname === `/org-settings` ||
      pathname === `/team-settings` ||
      pathname === `/org-settings` ||
      pathname === `/m-record` ||
      pathname === `/teams` ||
      pathname === `/all-teams` ||
      pathname === `/manage-members` ||
      pathname === `/sales-org` ||
      pathname === `/pricing` ||
      pathname === `/privacy` ||
      pathname === `/tos` ||
      pathname === `/playbook` ||
      pathname === `/integrations` ||
      pathname === `/preferences` ||
      pathname === `/practice-calls-vapi` ||
      pathname === `/profile` ||
      pathname === `/discovery-template` ||
      pathname === `/deal-discovery` ||
      pathname === `/mailbox` ||
      pathname === `/action-plan` ||
      pathname === `/meeting-prep` ||
      pathname === `/deals` ||
      pathname === `/interaction` ||
      pathname === `/unlinked-items` ||
      pathname === `/companies` ||
      pathname === `/reports` ||
      pathname.startsWith('/companies/') ||
      pathname === `/contacts` ||
      pathname.startsWith('/contacts/') ||
      pathname === `/practice/contests`) &&
      (title === 'Practice' ||
        title.trim() === 'CRO Interviews' ||
        title.trim() === 'Accounts' ||
        title.trim() === 'Compass Framework' ||
        title.trim() === 'Compass Helps' ||
        title.trim() === 'Sales Call Analysis' ||
        title.trim() === 'Tasks' ||
        title.trim() === 'Ask AI' ||
        title.trim() === 'John Barrows' ||
        pinnedTeams?.find((team) => team?.name === title.trim()) ||
        title.trim() === `${currentTeamToManage?.name}` ||
        title.trim() ===
          `Ask ${userDetails?.custom?.expertBot?.title || 'Ron'}` ||
        title.trim() === 'Brynne Tillman' ||
        title.trim() === 'John Stopper' ||
        title.trim() === 'Private Roleplays' ||
        title.trim() === 'My Performance' ||
        title.trim() === 'Organization' ||
        title.trim() === 'Tools' ||
        title.trim() === 'Meet Recorder' ||
        // title.trim() === 'Recall Alternative' ||
        title.trim() === 'My Teams' ||
        title.trim() === 'My Team' ||
        // title.trim() === 'My ORG' ||
        title.trim() === 'Clients Dashboard' ||
        title.trim() === 'Practice Call v2(vapi)' ||
        title.trim() === 'My Organization' ||
        title.trim() === 'My Profile' ||
        title.trim() === 'Playbook' ||
        title.trim() === 'Integrations' ||
        title.trim() === 'Preferences' ||
        title.trim() === 'Setup' ||
        title.trim() === 'Deals' ||
        title.trim() === 'Sales Org' ||
        title.trim() === 'Action Plan' ||
        title.trim() === 'Meeting Prep' ||
        title.trim() === 'Action Items' ||
        title.trim() === 'Unlinked Items' ||
        title.trim() === 'Mailbox' ||
        title.trim() === 'Reports' ||
        title.trim() === loggedInUserTeamDetails?.name ||
        title.trim() === 'Settings'));

  // `standalone`: a top-level destination rather than a group -- one row
  // with no heading and no children. Expanded, it renders as that single
  // row; collapsed (and on the rail) its icon navigates straight to `href`,
  // with no flyout.
  const rowItems = standalone
    ? [
        {
          name: title?.trim(),
          href,
          startIcon,
          highlighted: isGroupActive,
          showCounts,
          standalone: true,
        },
      ]
    : items;

  // Groups are open unless the user has explicitly collapsed this one, so
  // an empty `navCollapsedGroups` means everything is expanded.
  const groupKey = title?.trim();
  // A running walkthrough overrides the user's collapse state: several tour
  // steps point at sub-items inside these groups, and `Collapse`'s
  // `unmountOnExit` would leave those targets absent from the DOM.
  const isGroupCollapsed =
    !standalone && !isTourRunning && Boolean(navCollapsedGroups?.[groupKey]);
  // The two-level sidebar's first level renders the same groups, as an icon
  // with its short label under it. It paints and opens flyouts exactly like
  // the collapsed one-level rail -- the flyouts only while the second level
  // is collapsed, since otherwise the panel beside it already lists the
  // children. With the panel open, a pick scrolls the panel instead.
  const isRail = variant === 'rail';
  const isCollapsible =
    !isRail && !standalone && hasVisibleItems && dashboardSidebarExpanded;

  const toggleGroup = () => {
    dispatch(
      updateUiState({
        key: 'navCollapsedGroups',
        value: { ...navCollapsedGroups, [groupKey]: !isGroupCollapsed },
      }),
    );
  };

  // The group's label after the org's nav renaming has been applied. Was
  // inlined in the expanded header; it is a variable now because the
  // collapsed rail's flyout has to title itself with the same string.
  const displayTitle = title?.replace(
    Object.keys(orgConfigs?.navBarTitles || {})?.find((feature) =>
      title?.includes(feature),
    ),
    orgConfigs?.navBarTitles?.[
      Object.keys(orgConfigs?.navBarTitles || {})?.find((feature) =>
        title?.includes(feature),
      )
    ]?.title || title,
  );

  // Same renaming for a child row: first the group's `subTitles` map, then
  // the top-level `navBarTitles` map. Lifted out of the expanded list so the
  // flyout labels its rows identically rather than re-deriving them.
  const resolveItemLabel = (item) =>
    item?.name
      ?.replace(
        Object.keys(
          orgConfigs?.navBarTitles?.[
            Object.keys(orgConfigs?.navBarTitles || {})?.find((feature) =>
              title?.includes(feature),
            )
          ]?.subTitles || {},
        )?.find((subFeature) => item?.name?.includes(subFeature)),
        orgConfigs?.navBarTitles?.[
          Object.keys(orgConfigs?.navBarTitles || {})?.find((feature) =>
            title?.includes(feature),
          )
        ]?.subTitles?.[
          Object.keys(
            orgConfigs?.navBarTitles?.[
              Object.keys(orgConfigs?.navBarTitles || {})?.find((feature) =>
                title?.includes(feature),
              )
            ]?.subTitles || {},
          )?.find((subFeature) => item?.name?.includes(subFeature))
        ]?.title || item?.name,
      )
      ?.replace(
        Object.keys(orgConfigs?.navBarTitles || {})?.find((feature) =>
          item?.name?.includes(feature),
        ),
        orgConfigs?.navBarTitles?.[
          Object.keys(orgConfigs?.navBarTitles || {})?.find((feature) =>
            item?.name?.includes(feature),
          )
        ]?.title || item?.name,
      );

  const isChildActive = (item) =>
    item?.highlighted || `${pathname}${search}` === item?.href;

  // ------------------------------------------------------------- flyout ---
  // Collapsed, a group row is a bare icon and its children have nowhere to
  // render, so they move into a flyout anchored to the rail.
  //
  // Note this deliberately does *not* reuse `hasVisibleItems`: that flag is
  // gated on a pathname allowlist, which made sense for an inline list that
  // would otherwise push the rest of the nav down the page. A flyout costs
  // nothing until it is opened, so it only asks whether there is anything to
  // put in it.
  const flyoutItems = standalone ? [] : flyoutItemsOf(items);
  const canFlyout = !dashboardSidebarExpanded && flyoutItems.length > 0;

  // `null` when closed, otherwise how it was opened: a hover flyout follows
  // the pointer and closes when it leaves, a clicked one stays put until it
  // is dismissed.
  const [flyoutMode, setFlyoutMode] = useState(null);
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const closeFlyout = useCallback(() => {
    cancelClose();
    setFlyoutMode(null);
    setFlyoutAnchor(null);
  }, []);

  // A short grace period so the pointer can cross the gutter between the rail
  // and the flyout without the flyout vanishing under it.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(closeFlyout, 140);
  };

  const openFlyout = (anchor, mode) => {
    cancelClose();
    setFlyoutAnchor(anchor);
    // A hover must never downgrade a flyout the user opened by clicking.
    setFlyoutMode((current) => (current === 'click' ? current : mode));
  };

  useEffect(() => cancelClose, []);

  // Expanding the sidebar puts the children back inline, and a navigation
  // has already done what the flyout was opened for.
  useEffect(() => {
    if (dashboardSidebarExpanded) {
      closeFlyout();
    }
  }, [dashboardSidebarExpanded, closeFlyout]);

  useEffect(() => {
    closeFlyout();
  }, [pathname, search, closeFlyout]);

  useEffect(() => {
    if (!flyoutMode) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeFlyout();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [flyoutMode, closeFlyout]);

  const goTo = (target, after) => {
    dispatch(updateUiState({ key: 'currentContextData', value: null }));
    if (target !== window.location.href.split(window.location.origin)?.[1]) {
      history.push(target);
    }
    after?.();
    onNavClick?.();
    closeFlyout();
  };

  // The rail variant's tile: no fill of its own. The selected group -- the
  // route's, or one picked on the rail while the panel is open -- gets a soft
  // primary tint behind its icon and a primary-tinted icon; the others tint
  // the icon's square on hover.
  // A rail tile's click goes to the group's first page (its first child,
  // falling back to the group's own destination). With the panel open, it
  // also scrolls the panel to the group; collapsed, the panel stays shut.
  const railFirstItem = flyoutItems.find((item) => item?.href);
  const handleRailActivate = () => {
    if (dashboardSidebarExpanded) {
      onRailSelect?.();
    }
    if (railFirstItem) {
      goTo(railFirstItem.href, railFirstItem.onClick);
    } else if (href) {
      goTo(href);
    } else {
      closeFlyout();
    }
  };

  const railRowSx = {
    width: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.5,
    padding: '8px 2px',
    borderRadius: 2,
    cursor: 'pointer',
    textAlign: 'center',
    color: railSelected
      ? theme.palette.text.primary
      : theme.palette.text.secondary,
    ...(railSelected
      ? {}
      : {
          '&:hover .rail-icon': {
            backgroundColor: theme.palette.custom.primarySubtle,
          },
        }),
    '&:focus-visible': { outline: 'none' },
    '&:focus-visible .rail-icon': {
      outline: `2px solid ${theme.palette.custom.primaryText}`,
    },
  };

  return (
    <Box
      // The rail's copy of a group leaves the id, the tour target and the
      // scroll anchor to the panel's, so none of them is duplicated. The
      // tour target is the exception while the panel is collapsed: it is not
      // rendered then, so the rail tile is the only thing to point at.
      id={isRail ? undefined : id}
      className={isRail ? undefined : id}
      data-tour={!isRail || !dashboardSidebarExpanded ? dataTour : undefined}
      // The two-level sidebar's rail scrolls the nav to this anchor.
      data-nav-group={isRail ? undefined : navGroup}
      data-rail-group={isRail ? navGroup : undefined}
      data-nav-active={isGroupActive || undefined}
    >
      {standalone && !isRail && dashboardSidebarExpanded ? null : (
        <Tooltip
          // Collapsed, the row is a bare icon with no label beside it, so the
          // tooltip is the only thing naming it. Expanded, the label is right
          // there in the row and a tooltip would just repeat it.
          //
          // An empty title is MUI's own "do not render" signal, which is what
          // suppresses the tooltip while the flyout is open: the two would
          // otherwise land on the same spot to the right of the rail, and the
          // flyout already carries the group's name in its header.
          // The rail variant carries its label under the icon, so it needs
          // no tooltip either.
          title={
            !isRail && !dashboardSidebarExpanded && !flyoutMode
              ? displayTitle?.trim() || ''
              : ''
          }
          enterDelay={300}
          enterNextDelay={300}
          arrow
          placement="right"
        >
          <Box
            data-trackid={`open_${title}`}
            sx={
              isRail
                ? railRowSx
                : {
                    padding: dashboardSidebarExpanded
                      ? '12px 2px'
                      : '12px 2px 12px 18px',
                    textAlign: 'center',
                    borderRadius: 2,
                    background: 'transparent',
                    // Expanded, the header is now the collapse toggle, so it is a
                    // pointer whenever there is something to collapse. Collapsed, it
                    // is the group's own nav target, as before.
                    cursor:
                      !dashboardSidebarExpanded || isCollapsible
                        ? 'pointer'
                        : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    ...(isCollapsible
                      ? { ':hover': hoverNavSurface(theme) }
                      : {}),
                    // Only the collapsed rail paints its group rows: when the sidebar
                    // is expanded the group is a section heading, and the highlight
                    // belongs to the child row underneath it.
                    ...(!dashboardSidebarExpanded && isGroupActive
                      ? activeNavSurface(theme)
                      : {}),
                    ...(!dashboardSidebarExpanded && !isGroupActive
                      ? { ':hover': hoverNavSurface(theme) }
                      : {}),
                  }
            }
            role={isRail || isCollapsible || canFlyout ? 'button' : undefined}
            tabIndex={isRail || isCollapsible || canFlyout ? 0 : undefined}
            aria-current={isRail && railSelected ? 'true' : undefined}
            aria-expanded={
              isCollapsible
                ? !isGroupCollapsed
                : canFlyout
                ? Boolean(flyoutMode)
                : undefined
            }
            aria-haspopup={canFlyout ? 'menu' : undefined}
            aria-label={canFlyout ? displayTitle?.trim() : undefined}
            // Hovering only opens the group the user is already inside, so
            // sweeping the pointer down the rail does not throw a flyout for
            // every icon it passes.
            // The rail's tiles float any group's options while the panel is
            // collapsed -- each tile is labelled, so a sweep down the rail reads
            // as browsing rather than noise; with the panel open they do
            // nothing on hover (`canFlyout` is false then).
            onMouseEnter={(e) => {
              if (canFlyout && (isRail || isGroupActive)) {
                openFlyout(e.currentTarget, 'hover');
              }
            }}
            onMouseLeave={() => {
              if (flyoutMode === 'hover') {
                scheduleClose();
              }
            }}
            onKeyDown={(e) => {
              if (isRail && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleRailActivate();
                return;
              }
              if (isCollapsible && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                toggleGroup();
                return;
              }
              if (canFlyout && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                openFlyout(e.currentTarget, 'click');
                return;
              }
              if (e.key === 'Escape' && flyoutMode) {
                closeFlyout();
              }
            }}
            onClick={(e) => {
              handleTrackEvent(e, `open_${title}`);
              if (isRail) {
                handleRailActivate();
                return;
              }
              if (dashboardSidebarExpanded) {
                // Expanded, the header no longer navigates -- its children are
                // right underneath it, so the only thing left for it to do is
                // open and close the group.
                if (isCollapsible) {
                  toggleGroup();
                }
                return;
              }
              // Collapsed rail: the children have nowhere to go inline, so the
              // row opens them in a flyout. It pins open (mode 'click') even if
              // hover had already floated it, so the pointer can leave the rail
              // to reach it. The group's own destination is the flyout's title
              // row, so nothing is lost by not navigating here.
              if (canFlyout) {
                openFlyout(e.currentTarget, 'click');
                return;
              }
              // No children to show: the row is the group's nav target, as it
              // has always been.
              if (href) {
                goTo(href);
              } else {
                action();
              }
            }}
          >
            {isRail ? (
              <Box
                className="rail-icon"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: railSelected
                    ? theme.palette.custom.primarySoft
                    : 'transparent',
                  transition: theme.transitions.create('background-color', {
                    duration: theme.transitions.duration.shortest,
                  }),
                  // The group icons are coloured for the one-level rail's
                  // solid active fill (white when active). The rail has no such
                  // fill, so it sets the icon colour itself; `!important`
                  // because the icons carry their colour as an inline style.
                  '& svg': {
                    color: `${
                      railSelected
                        ? theme.palette.custom.primaryText
                        : theme.palette.text.secondary
                    } !important`,
                  },
                }}
              >
                {startIcon}
              </Box>
            ) : null}
            {!isRail && !dashboardSidebarExpanded ? (
              typeof startIcon === 'string' ? (
                <img
                  src={startIcon}
                  alt=""
                  style={{
                    filter: pathRegex.test(pathname) ? 'none' : 'invert(0.5)',
                    width: 20,
                    height: 20,
                  }}
                />
              ) : (
                startIcon
              )
            ) : null}
            {isRail ? (
              <Typography
                component="span"
                // `caption` is the type scale's smallest step (12px); the rail
                // is sized so "My Org" fits under the icon at it.
                variant="caption"
                sx={{
                  lineHeight: 1.2,
                  fontWeight: railSelected ? 700 : 500,
                  color: 'inherit',
                  // Up to two lines, so a two-word label ("Action Plan")
                  // wraps under the icon rather than losing its second word
                  // to an ellipsis.
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  maxWidth: 1,
                  overflow: 'hidden',
                  letterSpacing: 0.5,
                }}
              >
                {railLabel || displayTitle?.trim()}
              </Typography>
            ) : null}
            {!isRail && dashboardSidebarExpanded ? (
              <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'space-between'}
                width={1}
                gap={1}
                position={'relative'}
              >
                <Tooltip
                  arrow
                  placement="right"
                  // title={
                  //   title?.replace(
                  //     Object.keys(
                  //       orgConfigs?.navBarTitles || {},
                  //     )?.find((feature) => title?.includes(feature)),
                  //     orgConfigs?.navBarTitles?.[
                  //       Object.keys(
                  //         orgConfigs?.navBarTitles || {},
                  //       )?.find((feature) => title?.includes(feature))
                  //     ]?.title || title,
                  //   )?.length > 10
                  //     ? title?.replace(
                  //         Object.keys(
                  //           orgConfigs?.navBarTitles || {},
                  //         )?.find((feature) => title?.includes(feature)),
                  //         orgConfigs?.navBarTitles?.[
                  //           Object.keys(
                  //             orgConfigs?.navBarTitles || {},
                  //           )?.find((feature) => title?.includes(feature))
                  //         ]?.title || title,
                  //       )
                  //     : ''
                  // }
                  title=""
                >
                  <Typography
                    // `overline` is the theme's compact-uppercase-label role:
                    // 12px/700 with tracking. This was `body1` at 700, i.e. a
                    // 15px bold line competing with the nav items it labels.
                    //
                    // The colour was `text.disabled` -- a token reserved for
                    // controls that cannot be used, and the palette's faintest
                    // grey (#AEB5C5, 2.2:1 on the sidebar). These labels are not
                    // disabled, they are secondary, so they read `text.secondary`
                    // and clear AA.
                    variant={'overline'}
                    sx={{
                      color: theme.palette.text.muted,
                      // No `fontSize`/`fontWeight` here: `overline` is already
                      // 12px/700. The ternary this replaced sat inside the
                      // `dashboardSidebarExpanded` branch, so its other arm was
                      // unreachable and it always resolved to 700 anyway.
                      marginLeft: href === '/posted-jobs' ? 1.5 : 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      // Was a fixed 150px, sized for the old 240px sidebar; it
                      // now takes whatever the sidebar gives it so a wider
                      // sidebar actually buys longer labels.
                      flexGrow: 1,
                      minWidth: 0,
                      textAlign: 'left',
                    }}
                  >
                    {displayTitle}
                  </Typography>
                </Tooltip>
                {showCounts ? (
                  <Chip
                    label={showCounts}
                    size="small"
                    sx={{
                      position: 'absolute',
                      right: -8,
                      top: 0,
                    }}
                  />
                ) : null}
                {isCollapsible ? (
                  <ExpandMore
                    sx={{
                      fontSize: 18,
                      flexShrink: 0,
                      color: theme.palette.text.secondary,
                      // Pointing down when open, up when closed -- the chevron
                      // shows what the group is doing, not what clicking does.
                      transform: isGroupCollapsed
                        ? 'rotate(-90deg)'
                        : 'rotate(0deg)',
                      transition: theme.transitions.create('transform', {
                        duration: theme.transitions.duration.shortest,
                      }),
                    }}
                  />
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Tooltip>
      )}
      {canFlyout ? (
        <Popper
          open={Boolean(flyoutMode && flyoutAnchor)}
          anchorEl={flyoutAnchor}
          placement="right-start"
          transition
          modifiers={[
            // No `offset` distance: the gap between the rail and the panel is
            // padding on the wrapper below, so it stays inside the popper and
            // the pointer can cross it without tripping `onMouseLeave`.
            { name: 'offset', options: { offset: [-8, 0] } },
            { name: 'preventOverflow', options: { padding: 8 } },
          ]}
          sx={{ zIndex: theme.zIndex.drawer + 2 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={120}>
              <Box
                data-testid="sidebar-nav-flyout"
                onMouseEnter={cancelClose}
                onMouseLeave={() => {
                  if (flyoutMode === 'hover') {
                    scheduleClose();
                  }
                }}
                sx={{ paddingLeft: '10px' }}
              >
                <ClickAwayListener
                  onClickAway={(event) => {
                    // The row that opened the flyout is not "away" from it --
                    // without this, its click would close the panel the same
                    // click had just asked for.
                    if (flyoutAnchor?.contains(event.target)) {
                      return;
                    }
                    closeFlyout();
                  }}
                >
                  <Paper
                    elevation={8}
                    role="menu"
                    sx={{
                      minWidth: 216,
                      maxWidth: 280,
                      maxHeight: 'calc(100vh - 32px)',
                      overflowY: 'auto',
                      padding: 0.75,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.custom.border}`,
                      backgroundColor: theme.palette.custom.sidebar,
                    }}
                  >
                    {/* The group's own destination. The collapsed row used to
                        be the only way to reach it; now that the row opens
                        this panel instead, the title row carries it. */}
                    <Box
                      role={href ? 'menuitem' : undefined}
                      tabIndex={href ? 0 : undefined}
                      data-trackid={href ? `open_${title}` : undefined}
                      onClick={(e) => {
                        if (!href) {
                          return;
                        }
                        handleTrackEvent(e, `open_${title}`);
                        goTo(href);
                      }}
                      onKeyDown={(e) => {
                        if (href && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          goTo(href);
                        }
                      }}
                      sx={{
                        padding: '6px 10px',
                        borderRadius: 'var(--dc-radius-md)',
                        cursor: href ? 'pointer' : 'default',
                        ...(href ? { ':hover': hoverNavSurface(theme) } : {}),
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{
                          color: theme.palette.text.muted,
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {displayTitle}
                      </Typography>
                    </Box>
                    {flyoutItems.map((item) => {
                      const itemActive = isChildActive(item);
                      return (
                        <Box
                          key={item?.name}
                          role="menuitem"
                          tabIndex={0}
                          data-nav-active={itemActive || undefined}
                          data-trackid={`open_${item?.name}`}
                          onClick={(e) => {
                            handleTrackEvent(e, `open_${item?.name}`);
                            if (item?.href) {
                              goTo(item.href, item?.onClick);
                            } else {
                              action();
                              closeFlyout();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') {
                              return;
                            }
                            e.preventDefault();
                            if (item?.href) {
                              goTo(item.href, item?.onClick);
                            } else {
                              action();
                              closeFlyout();
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            padding: '8px 10px',
                            borderRadius: 'var(--dc-radius-md)',
                            cursor: 'pointer',
                            // A pinned deal keeps the indent guide it has in
                            // the expanded list, so the two readings of the
                            // same group stay recognisable as each other.
                            ...(item?.nested
                              ? {
                                  marginLeft: '12px',
                                  borderLeft: `1px solid ${theme.palette.custom.border}`,
                                  borderTopLeftRadius: 0,
                                  borderBottomLeftRadius: 0,
                                }
                              : {}),
                            transition: theme.transitions.create(
                              ['background'],
                              { duration: theme.transitions.duration.shortest },
                            ),
                            ...(itemActive
                              ? activeNavSurface(theme)
                              : { ':hover': hoverNavSurface(theme) }),
                          }}
                        >
                          {typeof item?.startIcon === 'string' ? (
                            item?.startIcon?.trim() ? (
                              <img
                                src={item.startIcon}
                                alt=""
                                style={{
                                  filter: itemActive ? 'none' : 'invert(0.5)',
                                  width: 20,
                                  height: 20,
                                  flexShrink: 0,
                                }}
                              />
                            ) : null
                          ) : (
                            item?.startIcon
                          )}
                          <Typography
                            variant={item?.nested ? 'body2' : 'body1'}
                            sx={{
                              color: itemActive
                                ? theme.palette.primary.contrastText
                                : theme.palette.text.primary,
                              flexGrow: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              textAlign: 'left',
                            }}
                          >
                            {resolveItemLabel(item)}
                          </Typography>
                          {item?.archived ? (
                            <Typography
                              variant="caption"
                              sx={{
                                flexShrink: 0,
                                color: itemActive
                                  ? theme.palette.primary.contrastText
                                  : theme.palette.text.muted,
                              }}
                            >
                              Inactive
                            </Typography>
                          ) : null}
                          {item?.showCounts ? (
                            <Chip
                              label={item.showCounts}
                              size="small"
                              sx={{
                                flexShrink: 0,
                                background: itemActive
                                  ? alpha('#FFFFFF', 0.22)
                                  : theme.palette.background.default,
                                border: `1px solid ${
                                  itemActive
                                    ? alpha('#FFFFFF', 0.35)
                                    : theme.palette.custom.border
                                }`,
                                color: itemActive
                                  ? theme.palette.primary.contrastText
                                  : theme.palette.text.primary,
                                height: 20,
                                '& .MuiChip-label': {
                                  padding: '0 6px',
                                },
                              }}
                            />
                          ) : null}
                        </Box>
                      );
                    })}
                  </Paper>
                </ClickAwayListener>
              </Box>
            </Fade>
          )}
        </Popper>
      ) : null}
      <Collapse in={!isGroupCollapsed} timeout="auto" unmountOnExit>
        {!isRail && hasVisibleItems && dashboardSidebarExpanded
          ? rowItems?.map((item) => {
              const isItemActive = isChildActive(item);
              return (
                <Box
                  role="link"
                  id={item?.id || ''}
                  key={item?.name}
                  data-tour={item?.dataTour}
                  data-nav-active={isItemActive || undefined}
                  sx={{
                    display: item?.disabled ? 'none' : 'block',
                    // The indent guide for a nested run (pinned deals under
                    // All Deals). The rule lives on each row rather than on a
                    // wrapper around the run, because the rows are one flat
                    // `items` array -- stacked with no gap, consecutive
                    // borders read as one continuous line.
                    ...(item?.nested
                      ? {
                          marginLeft: '24px',
                          paddingLeft: '10px',
                          borderLeft: `1px solid ${theme.palette.custom.border}`,
                        }
                      : {}),
                  }}
                >
                  <Tooltip
                    key={item?.name}
                    title={dashboardSidebarExpanded ? '' : item?.name}
                    arrow
                    placement="right"
                  >
                    <Box
                      sx={{
                        padding: '8px',
                        paddingLeft: item?.nested
                          ? 0
                          : item?.startIcon
                          ? '8px'
                          : '4px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        // A nested row takes its indent from the guide on the
                        // wrapper above, so it starts flush inside it.
                        // A standalone row is a peer of the group headings,
                        // so it is not indented under one.
                        marginLeft:
                          item?.nested || item?.standalone
                            ? 0
                            : item?.startIcon
                            ? '16px'
                            : '4px',
                        marginRight: '4px',
                        gap: 1,
                        background: 'transparent',
                        borderRadius: 2,
                        position: 'relative',
                        transition: theme.transitions.create(['background'], {
                          duration: theme.transitions.duration.shortest,
                        }),
                        ...(isItemActive
                          ? activeNavSurface(theme)
                          : { ':hover': hoverNavSurface(theme) }),
                      }}
                      data-trackid={`open_${item?.name}`}
                      onClick={(e) => {
                        handleTrackEvent(e, `open_${item?.name}`);
                        if (item?.href) {
                          history.push(item?.href);
                          dispatch(
                            updateUiState({
                              key: 'currentContextData',
                              value: null,
                            }),
                          );
                          item?.onClick?.();
                        } else {
                          action();
                        }
                      }}
                    >
                      {typeof item?.startIcon === 'string' ? (
                        item?.startIcon?.trim() ? (
                          <img
                            src={item?.startIcon}
                            alt=""
                            style={{
                              filter: pathRegex.test(pathname)
                                ? 'none'
                                : 'invert(0.5)',
                              width: 20,
                              height: 20,
                            }}
                          />
                        ) : (
                          <>&nbsp;</>
                        )
                      ) : (
                        item?.startIcon
                      )}
                      {dashboardSidebarExpanded ? (
                        <>
                          <Tooltip
                            title={
                              item?.adminTooltip
                                ? item.adminTooltip
                                : item?.name?.split('')?.length > 20
                                ? `${item?.name} ${
                                    item?.archived ? '(Inactive)' : ''
                                  }`
                                : `${item?.archived ? 'Inactive' : ''}`
                            }
                            arrow
                            placement="right"
                          >
                            <Typography
                              // A nested row (a pinned deal) steps down to the
                              // theme's secondary body size, so it reads as
                              // hanging off the row above rather than as
                              // another peer in the group. `body2` rather than
                              // a fractional `fontSize` keeps it on the type
                              // scale.
                              variant={item?.nested ? 'body2' : 'body1'}
                              sx={{
                                color: isItemActive
                                  ? theme.palette.primary.contrastText
                                  : theme.palette.text.primary,
                                // fontWeight: isItemActive ? 600 : 500,
                                // Long names used to be clipped by an absolutely
                                // positioned "..." painted over an opaque patch
                                // of the row colour. That patch cannot sit on the
                                // active pill's gradient without showing as a
                                // flat block (and it hard-coded `#fff` for the
                                // inactive case, which broke in dark mode), so
                                // the row now truncates natively instead.
                                display: 'block',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                width: '100%',
                                textAlign: 'left',
                                fontSize: item?.nested ? 13.5 : 15,
                              }}
                            >
                              {resolveItemLabel(item)}
                            </Typography>
                          </Tooltip>
                          {item?.showCounts ? (
                            <Chip
                              label={item?.showCounts}
                              size="small"
                              sx={{
                                // On the active pill the badge is a translucent
                                // well rather than a second solid indigo blob.
                                background: isItemActive
                                  ? alpha('#FFFFFF', 0.22)
                                  : theme.palette.background.default,
                                border: `1px solid ${
                                  isItemActive
                                    ? alpha('#FFFFFF', 0.35)
                                    : theme.palette.custom.border
                                }`,
                                position: 'absolute',
                                right: 10,
                                top: 7,
                                color: isItemActive
                                  ? theme.palette.primary.contrastText
                                  : theme.palette.text.primary,
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                minWidth: 0,
                                '& .MuiChip-label': {
                                  padding: 0,
                                },
                              }}
                            />
                          ) : null}
                        </>
                      ) : null}
                    </Box>
                  </Tooltip>
                  {pathname === `/accounts` &&
                  item?.name?.trim() === 'All Accounts' &&
                  dashboardSidebarExpanded ? (
                    currentContextData?.title ? (
                      <>
                        <Tooltip
                          title={
                            dashboardSidebarExpanded
                              ? ''
                              : currentContextData?.title
                          }
                          arrow
                          placement="right"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              // background:
                              //   `${pathname}${search}` === item?.href
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // borderRight:
                              //   `${pathname}${search}` === item?.href
                              //     ? `2px solid ${theme.palette.primary.light}`
                              //     : 'none',
                              // marginBottom: 1,
                              cursor: 'pointer',
                              // ':hover': {
                              //   background: theme.palette.background.level1,
                              // },
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 4,
                              gap: 1,
                            }}
                            data-trackid={`open_${currentContextData?.title}`}
                            onClick={(e) => {
                              handleTrackEvent(
                                e,
                                `open_${currentContextData?.title}`,
                              );
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    title: currentContextData?.title,
                                    tab: 'view',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: currentContextData?.title
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                                fontWeight: currentContextData?.title
                                  ? 600
                                  : 500,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                                '&::after': {
                                  content:
                                    currentContextData?.title?.split('')
                                      ?.length > 20
                                      ? '"..."'
                                      : '""',
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                  padding: '0 5px',
                                  background: currentContextData?.title
                                    ? theme.palette.background.level1
                                    : 'transparent',
                                },
                              }}
                            >
                              {currentContextData?.title}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip
                          title={dashboardSidebarExpanded ? '' : 'Overview'}
                          arrow
                          placement="right"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 6,
                              gap: 1,
                            }}
                            data-trackid={`open_overview`}
                            onClick={(e) => {
                              handleTrackEvent(e, `open_overview`);
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    title: currentContextData?.title,
                                    tab: 'view',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  currentContextData?.tab === 'view'
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                fontWeight:
                                  currentContextData?.tab === 'view'
                                    ? 600
                                    : 500,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                              }}
                            >
                              Overview
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip
                          title={dashboardSidebarExpanded ? '' : 'Compass'}
                          arrow
                          placement="right"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 6,
                              gap: 1,
                            }}
                            data-trackid={`open_compass`}
                            onClick={(e) => {
                              handleTrackEvent(e, `open_compass`);
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    title: currentContextData?.title,
                                    tab: 'manage',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  currentContextData?.tab === 'manage'
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                fontWeight:
                                  currentContextData?.tab === 'manage'
                                    ? 600
                                    : 500,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                              }}
                            >
                              Compass
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip
                          title={
                            dashboardSidebarExpanded ? '' : 'Organization Chart'
                          }
                          arrow
                          placement="right"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 6,
                              gap: 1,
                            }}
                            data-trackid={`open_org_chart`}
                            onClick={(e) => {
                              handleTrackEvent(e, `open_org_chart`);
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    title: currentContextData?.title,
                                    tab: 'orgchart',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  currentContextData?.tab === 'orgchart'
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                fontWeight:
                                  currentContextData?.tab === 'orgchart'
                                    ? 600
                                    : 500,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                              }}
                            >
                              Org Chart
                            </Typography>
                          </Box>
                        </Tooltip>
                      </>
                    ) : null
                  ) : null}
                  {/* {pathname === `/teams` &&
                  item?.name?.trim() === 'All Teams' &&
                  dashboardSidebarExpanded ? (
                    currentContextData?.currentTeam ? (
                      <>
                        <Tooltip
                          title={
                            dashboardSidebarExpanded
                              ? ''
                              : currentContextData?.currentTeam
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              // background:
                              //   `${pathname}${search}` === item?.href
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // borderRight:
                              //   `${pathname}${search}` === item?.href
                              //     ? `2px solid ${theme.palette.primary.light}`
                              //     : 'none',
                              // marginBottom: 1,
                              cursor: 'pointer',
                              // ':hover': {
                              //   background: theme.palette.background.level1,
                              // },
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 3,
                              gap: 1,
                            }}
                            data-trackid={`open_${currentContextData?.currentTeam}`}
                            onClick={(e) => {
                              handleTrackEvent(
                                e,
                                `open_${currentContextData?.currentTeam}`,
                              );
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    currentTeam: currentContextData?.currentTeam,
                                    currentTeamTab: 'manageusers',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              sx={{
                                color: currentContextData?.currentTeam
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                                fontWeight: currentContextData?.currentTeam
                                  ? 600
                                  : 500,
                                fontSize: 14,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                                '&::after': {
                                  content:
                                    currentContextData?.currentTeam?.split('')
                                      ?.length > 20
                                      ? '"..."'
                                      : '""',
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                  padding: '0 5px',
                                  background: currentContextData?.currentTeam
                                    ? 'var(--dc-sidebar)'
                                    : 'transparent',
                                },
                              }}
                            >
                              {currentContextData?.currentTeam}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip
                          title={
                            dashboardSidebarExpanded ? '' : 'Score Dashboard'
                          }
                          arrow
                          placement="right"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              // background:
                              //   `${pathname}${search}` === item?.href
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // borderRight:
                              //   `${pathname}${search}` === item?.href
                              //     ? `2px solid ${theme.palette.primary.light}`
                              //     : 'none',
                              // marginBottom: 1,
                              cursor: 'pointer',
                              // ':hover': {
                              //   background: theme.palette.background.level1,
                              // },
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 5,
                              gap: 1,
                            }}
                            data-trackid={`open_score_dashboard`}
                            onClick={(e) => {
                              handleTrackEvent(e, `open_score_dashboard`);
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    currentTeam: currentContextData?.currentTeam,
                                    currentTeamTab: 'scoresboard',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  currentContextData?.currentTeamTab ===
                                  'scoresboard'
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                fontWeight:
                                  currentContextData?.currentTeamTab ===
                                  'scoresboard'
                                    ? 600
                                    : 500,
                                fontSize: 14,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                              }}
                            >
                              Score Dashboard
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip
                          title={dashboardSidebarExpanded ? '' : 'Team Dashboard'}
                          arrow
                          placement="right"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              // background:
                              //   `${pathname}${search}` === item?.href
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // borderRight:
                              //   `${pathname}${search}` === item?.href
                              //     ? `2px solid ${theme.palette.primary.light}`
                              //     : 'none',
                              // marginBottom: 1,
                              cursor: 'pointer',
                              // ':hover': {
                              //   background: theme.palette.background.level1,
                              // },
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 5,
                              gap: 1,
                            }}
                            data-trackid={`open_manage_members`}
                            onClick={(e) => {
                              handleTrackEvent(e, `open_manage_members`);
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    currentTeam: currentContextData?.currentTeam,
                                    currentTeamTab: 'manageusers',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  currentContextData?.currentTeamTab ===
                                  'manageusers'
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                fontWeight:
                                  currentContextData?.currentTeamTab ===
                                  'manageusers'
                                    ? 600
                                    : 500,
                                fontSize: 14,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                              }}
                            >
                              Team Dashboard
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Tooltip
                          title={dashboardSidebarExpanded ? '' : 'Team Settings'}
                          arrow
                          placement="right"
                        >
                          <Box
                            sx={{
                              padding: '8px 32px',
                              textAlign: 'center',
                              // background:
                              //   `${pathname}${search}` === item?.href
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // borderRight:
                              //   `${pathname}${search}` === item?.href
                              //     ? `2px solid ${theme.palette.primary.light}`
                              //     : 'none',
                              // marginBottom: 1,
                              cursor: 'pointer',
                              // ':hover': {
                              //   background: theme.palette.background.level1,
                              // },
                              display: 'flex',
                              alignItems: 'center',
                              marginLeft: 5,
                              gap: 1,
                            }}
                            data-trackid={`open_team_settings`}
                            onClick={(e) => {
                              handleTrackEvent(e, `open_team_settings`);
                              dispatch(
                                updateUiState({
                                  key: 'currentContextData',
                                  value: {
                                    currentTeam: currentContextData?.currentTeam,
                                    currentTeamTab: 'teamsettings',
                                  },
                                }),
                              );
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  currentContextData?.currentTeamTab ===
                                  'teamsettings'
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                fontWeight:
                                  currentContextData?.currentTeamTab ===
                                  'teamsettings'
                                    ? 600
                                    : 500,
                                fontSize: 14,
                                display: 'flex',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                width: '100%',
                                position: 'relative',
                                // '&::after': {
                                //   content:
                                //     currentContextData?.currentTeam?.split('')?.length >
                                //     20
                                //       ? '"..."'
                                //       : '""',
                                //   position: 'absolute',
                                //   right: 0,
                                //   top: 0,
                                //   padding: '0 5px',
                                //   background: currentContextData?.currentTeam
                                //     ? theme.palette.background.level1
                                //     : 'transparent',
                                // },
                              }}
                            >
                              Team Settings
                            </Typography>
                          </Box>
                        </Tooltip>
                      </>
                    ) : null
                  ) : null} */}
                  {item?.name?.trim() === 'Organization Settings' &&
                  (pathname === '/settings' || pathname === '/pricing') &&
                  dashboardSidebarExpanded ? (
                    <>
                      {/* <Tooltip
                        title={dashboardSidebarExpanded ? '' : 'Playbooks'}
                        arrow
                        placement="right"
                      >
                        <Box
                          sx={{
                            padding: '8px 32px',
                            textAlign: 'center',
                            // background:
                            //   `${pathname}${search}` === item?.href
                            //     ? theme.palette.background.level1
                            //     : 'transparent',
                            // borderRight:
                            //   `${pathname}${search}` === item?.href
                            //     ? `2px solid ${theme.palette.primary.light}`
                            //     : 'none',
                            // marginBottom: 1,
                            cursor: 'pointer',
                            // ':hover': {
                            //   background: theme.palette.background.level1,
                            // },
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 5,
                            gap: 1,
                          }}
                          data-trackid={`open_org_playbook_settings`}
                          onClick={(e) => {
                            handleTrackEvent(e, `open_org_playbook_settings`);
                            history.push('/settings?t=playbook');
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                `${pathname}${search}` === '/settings?t=playbook'
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                              fontWeight:
                                `${pathname}${search}` === '/settings?t=playbook'
                                  ? 600
                                  : 500,
                              fontSize: 14,
                              display: 'flex',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              position: 'relative',
                              // '&::after': {
                              //   content:
                              //     currentContextData?.currentTeam?.split('')?.length >
                              //     20
                              //       ? '"..."'
                              //       : '""',
                              //   position: 'absolute',
                              //   right: 0,
                              //   top: 0,
                              //   padding: '0 5px',
                              //   background: currentContextData?.currentTeam
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // },
                            }}
                          >
                            Playbooks
                          </Typography>
                        </Box>
                      </Tooltip> */}
                      <Tooltip
                        title={dashboardSidebarExpanded ? '' : 'Integrations'}
                        arrow
                        placement="right"
                      >
                        <Box
                          sx={{
                            padding: '8px 32px',
                            textAlign: 'center',
                            // background:
                            //   `${pathname}${search}` === item?.href
                            //     ? theme.palette.background.level1
                            //     : 'transparent',
                            // borderRight:
                            //   `${pathname}${search}` === item?.href
                            //     ? `2px solid ${theme.palette.primary.light}`
                            //     : 'none',
                            // marginBottom: 1,
                            cursor: 'pointer',
                            // ':hover': {
                            //   background: theme.palette.background.level1,
                            // },
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 5,
                            gap: 1,
                          }}
                          data-trackid={`open_org_bots_listing`}
                          onClick={(e) => {
                            handleTrackEvent(e, `open_org_bots_listing`);
                            history.push(`/settings?t=integrations`);
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                `${pathname}${search}` ===
                                '/settings?t=integrations'
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                              fontWeight:
                                `${pathname}${search}` ===
                                '/settings?t=integrations'
                                  ? 600
                                  : 500,
                              display: 'flex',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              position: 'relative',
                              // '&::after': {
                              //   content:
                              //     currentContextData?.currentTeam?.split('')?.length >
                              //     20
                              //       ? '"..."'
                              //       : '""',
                              //   position: 'absolute',
                              //   right: 0,
                              //   top: 0,
                              //   padding: '0 5px',
                              //   background: currentContextData?.currentTeam
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // },
                            }}
                          >
                            Integrations
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Tooltip
                        title={dashboardSidebarExpanded ? '' : 'Ask Donna'}
                        arrow
                        placement="right"
                      >
                        <Box
                          sx={{
                            padding: '8px 32px',
                            textAlign: 'center',
                            // background:
                            //   `${pathname}${search}` === item?.href
                            //     ? theme.palette.background.level1
                            //     : 'transparent',
                            // borderRight:
                            //   `${pathname}${search}` === item?.href
                            //     ? `2px solid ${theme.palette.primary.light}`
                            //     : 'none',
                            // marginBottom: 1,
                            cursor: 'pointer',
                            // ':hover': {
                            //   background: theme.palette.background.level1,
                            // },
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 5,
                            gap: 1,
                          }}
                          data-trackid={`open_org_ask_donna`}
                          onClick={(e) => {
                            handleTrackEvent(e, `open_org_ask_donna`);
                            history.push(`/settings?t=donna-presets`);
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                `${pathname}${search}` ===
                                '/settings?t=donna-presets'
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                              fontWeight:
                                `${pathname}${search}` ===
                                '/settings?t=donna-presets'
                                  ? 600
                                  : 500,
                              display: 'flex',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              position: 'relative',
                              // '&::after': {
                              //   content:
                              //     currentContextData?.currentTeam?.split('')?.length >
                              //     20
                              //       ? '"..."'
                              //       : '""',
                              //   position: 'absolute',
                              //   right: 0,
                              //   top: 0,
                              //   padding: '0 5px',
                              //   background: currentContextData?.currentTeam
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // },
                            }}
                          >
                            Ask Donna
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Tooltip
                        title={dashboardSidebarExpanded ? '' : 'Plans'}
                        arrow
                        placement="right"
                      >
                        <Box
                          sx={{
                            padding: '8px 32px',
                            textAlign: 'center',
                            // background:
                            //   `${pathname}${search}` === item?.href
                            //     ? theme.palette.background.level1
                            //     : 'transparent',
                            // borderRight:
                            //   `${pathname}${search}` === item?.href
                            //     ? `2px solid ${theme.palette.primary.light}`
                            //     : 'none',
                            // marginBottom: 1,
                            cursor: 'pointer',
                            // ':hover': {
                            //   background: theme.palette.background.level1,
                            // },
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 5,
                            gap: 1,
                          }}
                          data-trackid={`open_org_ask_donna_settings`}
                          onClick={(e) => {
                            handleTrackEvent(e, `open_org_ask_donna_settings`);
                            history.push(`/pricing`);
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                `${pathname}${search}` === '/pricing'
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                              fontWeight:
                                `${pathname}${search}` === '/pricing'
                                  ? 600
                                  : 500,
                              display: 'flex',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              position: 'relative',
                              // '&::after': {
                              //   content:
                              //     currentContextData?.currentTeam?.split('')?.length >
                              //     20
                              //       ? '"..."'
                              //       : '""',
                              //   position: 'absolute',
                              //   right: 0,
                              //   top: 0,
                              //   padding: '0 5px',
                              //   background: currentContextData?.currentTeam
                              //     ? theme.palette.background.level1
                              //     : 'transparent',
                              // },
                            }}
                          >
                            Plans
                          </Typography>
                        </Box>
                      </Tooltip>
                    </>
                  ) : null}
                </Box>
              );
            })
          : null}
      </Collapse>
    </Box>
  );
};

NavItem.propTypes = {
  id: PropTypes.string,
  items: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  startIcon: PropTypes.any,
  onClose: PropTypes.func,
  action: PropTypes.func,
  showSecondaryItems: PropTypes.bool,
  disabled: PropTypes.bool,
  highlighted: PropTypes.bool,
  teamMode: PropTypes.string,
  forPinnedTeam: PropTypes.bool,
  team: PropTypes.object,
  showCounts: PropTypes.number,
  onNavClick: PropTypes.func,
  dataTour: PropTypes.string,
  navGroup: PropTypes.string,
  variant: PropTypes.oneOf(['rail']),
  railLabel: PropTypes.string,
  railSelected: PropTypes.bool,
  onRailSelect: PropTypes.func,
  standalone: PropTypes.bool,
};

export default NavItem;
